import { cloneElement, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TooltipColors from './TooltipColors';
import arrowPosition from './utils/arrowPosition';
import modifierSettings from './utils/modifierSettings';

const Tooltip = ({
  children,
  color = 'primary',
  colorProps,
  enterDelay = 300,
  leaveDelay = 25,
  maxWidth,
  minWidth,
  title,
  position = 'top',
  disableFlip,
  interactive,
}) => {
  const [open, setOpen] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const [arrowEl, setArrowEl] = useState(null);
  const anchorRef = useRef(null);
  const timer = useRef(null);

  const colors = colorProps ?? TooltipColors[color];
  const modifiers = [
    ...modifierSettings(),
    {
      name: 'arrow',
      enabled: true,
      options: { element: arrowEl, padding: 4 },
    },
    ...(disableFlip ? [{ name: 'flip', enabled: false }] : []),
  ];

  // Handles the timeout event for showing the tooltip.
  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setOpen(true);
    }, enterDelay);
  };

  // Handles the timeout event for hiding the tooltip.
  const hide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setOpen(false);
    }, leaveDelay);
  };

  // Loads the tooltip into the DOM on the first hover.
  const handleMouseEnter = (event) => {
    setHasHovered(true);
    show();
    children?.props?.onMouseEnter?.(event);
  };

  const handleMouseLeave = (event) => {
    hide();
    children?.props?.onMouseLeave?.();
  };

  const handleChildClick = (e) => {
    hide();
    // Ensure the child clicks are still invoked.
    children.props.onClick?.(e);
  };

  const childComponent = cloneElement(children, {
    ref: anchorRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleChildClick,
  });

  const handleModifiers = () => {
    return [];
  };

  return (
    <>
      {childComponent}
      {hasHovered && title && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={position}
          transition
          sx={{ pointerEvents: 'none', zIndex: 1300 }}
          modifiers={modifiers}
        >
          {({ TransitionProps, placement: actualPlacement }) => (
            <Fade {...TransitionProps} timeout={200}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    position: 'absolute',
                    overflow: 'hidden',
                    zIndex: 2,
                    ...arrowPosition(actualPlacement),
                  }}
                >
                  <div
                    ref={setArrowEl}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 8,
                      height: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        transform: `translateY(${actualPlacement.startsWith('bottom') ? 4 : -4}px) rotate(45deg)`,
                        backgroundColor: colors.backgroundColor,
                      }}
                    />
                  </div>
                </div>
                <Paper
                  elevation={2}
                  sx={{
                    ...colors,
                    fontSize: '0.75rem',
                    lineHeight: '1.25rem',
                    maxWidth: maxWidth ?? 'auto',
                    minWidth: minWidth ?? 'auto',
                    // py: 0.5,
                    px: 1,
                    mx: 'auto',
                    width: 'fit-content',
                    position: 'relative',
                  }}
                >
                  <Box position="relative" zIndex={1}>
                    {title}
                  </Box>
                </Paper>
              </div>
            </Fade>
          )}
        </Popper>
      )}
    </>
  );
};

export default Tooltip;
